import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroCheckbox,
	CerebroDatePicker,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import {
	PersonalGuaranteeTypes,
	PersonalGuaranteeTypesMap,
	TermSheet,
	TermSheetRevision,
	TermSheetTypes,
	TermSheetTypesMap,
	UpdateTermSheetRevisionDto,
	WarrantCoverageTypes,
	WarrantCoverageTypesMap,
} from '../../../shared/models';
import { DateUtils, FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import { MaxCurrencyLength } from '../../../shared/utils/platform';

interface GeneralFormProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	termSheet: TermSheet;
	revision: TermSheetRevision;
	isLoading: boolean;
}

export const GeneralForm: React.FC<GeneralFormProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	termSheet,
	revision,
	isLoading,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const generalMutation = useMutation(
		async ({ values }: GeneralFormMutationVariables) => {
			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const response = await getApiResponseData<void>(
					api.patch(
						CerebroApiEndpoints.TermSheets.ReplaceFile(dataRoomGuid, termSheetGuid, revisionGuid),
						formData,
						{
							headers: { 'Content-Type': 'multipart/form-data' },
						}
					)
				);

				if (response.error) {
					throw new Error(response.error.response?.data);
				}
			}

			const patchValues = {
				isIncumbentSolution: values.isIncumbentSolution,
				visibleToBorrower: values.visibleToBorrower,
				termSheetType: values.termSheetType,
				hasPersonalGuaranty: values.hasPersonalGuaranty,
				personalGuaranteeType: values.personalGuaranteeType,
				personalGuarantyDetails: values.personalGuarantyDetails,
				warrantRequired: values.warrantRequired,
				warrantAmount: NumberUtils.ParseDecimal(values.warrantAmount, undefined),
				isWarrantAmountTbd: values.isWarrantAmountTbd,
				warrantCoverageType: values.warrantCoverageType,
				warrantPrice: NumberUtils.ParseDecimal(values.warrantPrice, undefined),
				isWarrantPriceTbd: values.isWarrantPriceTbd,
				warrantDetails: values.warrantDetails,
				covenantsProvided: values.covenantsProvided,
				reportingProvided: values.reportingProvided,
				expirationDate: values.expirationDate ? values.expirationDate.toISOString().split('T')[0] : null,
				sendExpirationReminder: values.sendExpirationReminder,
				sourcePage: 'General',
			} as UpdateTermSheetRevisionDto;

			const response = await getApiResponseData<void>(
				api.patch(
					CerebroApiEndpoints.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid),
					patchValues
				)
			);

			if (response.error) {
				throw new Error(response.error.response?.data);
			}

			return patchValues;
		},
		{
			onSuccess: (data: UpdateTermSheetRevisionDto, { helpers }) => {
				snackbar.enqueueSnackbar('Term sheet revision was updated.', { variant: 'success' });
				helpers.resetForm({
					values: {
						isIncumbentSolution: data.isIncumbentSolution,
						visibleToBorrower: data.visibleToBorrower,
						termSheetType: data.termSheetType,
						hasPersonalGuaranty: data.hasPersonalGuaranty,
						personalGuaranteeType: data.personalGuaranteeType,
						personalGuarantyDetails: data.hasPersonalGuaranty ? data.personalGuarantyDetails : undefined,
						warrantRequired: data.warrantRequired,
						warrantAmount: NumberUtils.FormatFloat(revision.warrantAmount, 2),
						isWarrantAmountTbd: data.isWarrantAmountTbd,
						warrantCoverageType: data.warrantCoverageType,
						warrantPrice: NumberUtils.FormatNumberAsCurrencyString(data.warrantPrice, 2, 2, 2),
						isWarrantPriceTbd: data.isWarrantPriceTbd,
						warrantDetails: data.warrantDetails,
						expirationDate: data.expirationDate,
						sendExpirationReminder: data.sendExpirationReminder,
						uploads: [] as File[],
						covenantsProvided: data.covenantsProvided,
						reportingProvided: data.reportingProvided,
					},
				});
				client.invalidateQueries(QueryKeys.TermSheets.Specific(dataRoomGuid, termSheetGuid));
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<Formik
			initialValues={{
				isIncumbentSolution: termSheet.isIncumbentSolution,
				visibleToBorrower: revision.visibleToBorrower,
				termSheetType: revision.termSheetType,
				hasPersonalGuaranty: revision.hasPersonalGuaranty,
				personalGuaranteeType: revision.personalGuaranteeType,
				personalGuarantyDetails: revision.hasPersonalGuaranty ? revision.personalGuarantyDetails : undefined,
				warrantRequired: revision.warrantRequired,
				warrantAmount: NumberUtils.FormatFloat(revision.warrantAmount, 2),
				isWarrantAmountTbd: revision.isWarrantAmountTbd,
				warrantCoverageType: revision.warrantCoverageType,
				warrantPrice: NumberUtils.FormatNumberAsCurrencyString(revision.warrantPrice, 2, 2, 2),
				isWarrantPriceTbd: revision.isWarrantPriceTbd,
				warrantDetails: revision.warrantDetails,
				expirationDate: DateUtils.ParseDateOnly(revision.expirationDate),
				sendExpirationReminder: revision.sendExpirationReminder,
				uploads: [] as File[],
				covenantsProvided: revision.covenantsProvided,
				reportingProvided: revision.reportingProvided,
			}}
			validate={validateForm}
			onSubmit={FormikUtils.MutateOnSubmit(generalMutation.mutate)}
		>
			{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
				return (
					<CerebroForm
						flatIndicator
						encType="multipart/form-data"
						onSubmit={handleSubmit}
						sx={{ marginTop: 3, paddingRight: 3 }}
					>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel id="isIncumbentSolution">Incumbent Solution</CerebroLabel>
								<Field
									id="isIncumbentSolution"
									as={CerebroYesNo}
									name="isIncumbentSolution"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="visibleToBorrower">Visible to Borrower</CerebroLabel>
								<Field
									id="visibleToBorrower"
									as={CerebroYesNo}
									name="visibleToBorrower"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="termSheetType">Term Sheet Format</CerebroLabel>
								<Field
									id="termSheetType"
									as={CerebroDropdown}
									name="termSheetType"
									disabled={isSubmitting}
								>
									{Object.entries(TermSheetTypesMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="uploads">Term Sheet File</CerebroLabel>
								<Field
									as={CerebroFileDragAndDrop}
									name="uploads"
									onlyPdf={true}
									multiple={false}
									sx={{ marginY: 3, width: '100%' }}
									error={touched.uploads && errors.uploads !== undefined}
								/>
								{errors.uploads && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.uploads}
									</CerebroText>
								)}
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="hasPersonalGuaranty">Has Guaranty?</CerebroLabel>
								<Field
									id="hasPersonalGuaranty"
									as={CerebroYesNo}
									name="hasPersonalGuaranty"
									disabled={isSubmitting}
								/>
							</Grid>

							{values.hasPersonalGuaranty && (
								<>
									<Grid item xs={11}>
										<CerebroLabel id="personalGuaranteeType">Guarantee Entity</CerebroLabel>
										<Field
											id="personalGuaranteeType"
											as={CerebroDropdown}
											name="personalGuaranteeType"
											disabled={isSubmitting}
											error={
												touched.personalGuaranteeType &&
												errors.personalGuaranteeType !== undefined
											}
										>
											{Object.entries(PersonalGuaranteeTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{errors.personalGuaranteeType && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.personalGuaranteeType}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={11}>
										<CerebroLabel id="personalGuarantyDetails">
											Guarantee Details/Notes
										</CerebroLabel>
										<Field
											id="personalGuarantyDetails"
											as={CerebroTextarea}
											name="personalGuarantyDetails"
											disabled={isSubmitting}
											error={
												touched.personalGuarantyDetails &&
												errors.personalGuarantyDetails !== undefined
											}
										/>
										{errors.personalGuarantyDetails && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.personalGuarantyDetails}
											</CerebroText>
										)}
									</Grid>
								</>
							)}

							<Grid item xs={11}>
								<CerebroLabel id="covenantsProvided">
									Does the term sheet include covenants?
								</CerebroLabel>
								<Field
									id="covenantsProvided"
									as={CerebroYesNo}
									name="covenantsProvided"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="reportingProvided">Are reporting requirements included?</CerebroLabel>
								<Field
									id="reportingProvided"
									as={CerebroYesNo}
									name="reportingProvided"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="warrantRequired">
									Does the lender require equity warrants?
								</CerebroLabel>
								<Field
									id="warrantRequired"
									as={CerebroYesNo}
									name="warrantRequired"
									disabled={isSubmitting}
								/>
							</Grid>

							{values.warrantRequired && (
								<>
									<Grid item xs={6}>
										<CerebroLabel id="warrantAmount">
											What level of warrant coverage is required?
										</CerebroLabel>
										<Field
											id="warrantAmount"
											as={CerebroMaskedInput}
											allowDecimals={true}
											minDecimalPlaces={2}
											maxDecimalPlaces={2}
											name="warrantAmount"
											disabled={isSubmitting || values.isWarrantAmountTbd}
											value={values.warrantAmount}
											error={touched.warrantAmount && errors.warrantAmount !== undefined}
										/>
										{errors.warrantAmount && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.warrantAmount}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={6}>
										<CerebroLabel htmlFor="isWarrantAmountTbd" gutterBottom sx={{ marginTop: 4 }}>
											<Field
												id="isWarrantAmountTbd"
												name="isWarrantAmountTbd"
												as={CerebroCheckbox}
												disabled={isSubmitting}
												error={
													touched.isWarrantAmountTbd &&
													errors.isWarrantAmountTbd !== undefined
												}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													setFieldValue('isWarrantAmountTbd', e.target.checked);
													if (e.target.checked) {
														values.warrantAmount = '';
														setFieldValue('warrantAmount', '');
													}
												}}
											/>
											TBD
										</CerebroLabel>
									</Grid>
									<Grid item xs={11}>
										<CerebroLabel id="warrantCoverageType">
											Does the lender require equity warrants?
										</CerebroLabel>
										<Field
											id="warrantCoverageType"
											as={CerebroDropdown}
											name="warrantCoverageType"
											disabled={isSubmitting}
											error={
												touched.warrantCoverageType && errors.warrantCoverageType !== undefined
											}
										>
											{Object.entries(WarrantCoverageTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{errors.warrantCoverageType && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.warrantCoverageType}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={6}>
										<CerebroLabel id="warrantPrice">What is the price of the warrant?</CerebroLabel>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="warrantPrice"
											name="warrantPrice"
											minDecimalPlaces={2}
											maxDecimalPlaces={2}
											allowDecimals={true}
											disabled={isSubmitting || values.isWarrantPriceTbd}
											value={values.warrantPrice}
											error={touched.warrantPrice && errors.warrantPrice !== undefined}
										/>
										{errors.warrantPrice && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.warrantPrice}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={6}>
										<CerebroLabel htmlFor="isWarrantPriceTbd" gutterBottom sx={{ marginTop: 4 }}>
											<Field
												id="isWarrantPriceTbd"
												name="isWarrantPriceTbd"
												as={CerebroCheckbox}
												disabled={isSubmitting}
												error={
													touched.isWarrantPriceTbd && errors.isWarrantPriceTbd !== undefined
												}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													setFieldValue('isWarrantPriceTbd', e.target.checked);
													if (e.target.checked) {
														values.warrantPrice = '';
														setFieldValue('warrantPrice', '');
													}
												}}
											/>
											TBD
										</CerebroLabel>
									</Grid>
									<Grid item xs={11}>
										<CerebroLabel id="warrantDetails">Equity / Warrant Details</CerebroLabel>
										<Field
											id="warrantDetails"
											as={CerebroTextarea}
											name="warrantDetails"
											rows={10}
											disabled={isSubmitting}
										/>
									</Grid>
								</>
							)}

							<Grid item xs={11}>
								<CerebroLabel id="expirationDate">Expiration Date</CerebroLabel>
								<Field
									id="expirationDate"
									name="expirationDate"
									component={CerebroDatePicker}
									disabled={isSubmitting}
								/>
							</Grid>

							{values.expirationDate && (
								<Grid item xs={11}>
									<CerebroLabel id="sendExpirationReminder">Receive Email Alert?</CerebroLabel>
									<Field
										id="sendExpirationReminder"
										as={CerebroYesNo}
										name="sendExpirationReminder"
										disabled={isSubmitting}
									/>
									<CerebroText variant="hint">
										Email alert will be sent to the borrower &amp; transaction manager 3 days prior
										to expiration.
									</CerebroText>
								</Grid>
							)}

							<Grid item xs={8}>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Save
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				);
			}}
		</Formik>
	);
};

type GeneralFormMutationVariables = FormikUtils.MutateOnSubmitVariables<GeneralFormValues>;
interface GeneralFormValues {
	isIncumbentSolution: boolean;
	visibleToBorrower: boolean;
	termSheetType: TermSheetTypes | undefined;
	hasPersonalGuaranty: boolean;
	personalGuaranteeType: PersonalGuaranteeTypes | undefined;
	personalGuarantyDetails: string | undefined;
	warrantRequired: boolean;
	warrantAmount: string;
	isWarrantAmountTbd: boolean | undefined;
	warrantCoverageType: WarrantCoverageTypes | undefined;
	warrantPrice: string;
	isWarrantPriceTbd: boolean | undefined;
	warrantDetails: string;
	uploads: File[];
	covenantsProvided: boolean;
	reportingProvided: boolean;
	expirationDate: Date | null;
	sendExpirationReminder: boolean | undefined;
}
type GeneralFormValueErrors = FormikErrors<GeneralFormValues>;

function validateForm(values: GeneralFormValues) {
	const errors: GeneralFormValueErrors = {};

	if (values.hasPersonalGuaranty) {
		if (!values.personalGuaranteeType) {
			errors.personalGuaranteeType = 'You must enter which entity carries the guarantee.';
		}
	}

	if (values.warrantRequired) {
		if (!values.isWarrantAmountTbd) {
			if (!values.warrantAmount || values.warrantAmount === '') {
				errors.warrantAmount = 'You must enter a valid warrant amount.';
			}
		}

		if (!values.warrantCoverageType) {
			errors.warrantCoverageType = 'You must select whether the lender requires equity warrants.';
		}

		if (!values.isWarrantPriceTbd) {
			if (!values.warrantPrice || values.warrantPrice === '') {
				errors.warrantPrice = 'You must enter a valid warrant price.';
			}
		}
	}

	return errors;
}
